// src/client.ts
import axios from "axios";
import axiosRetry from "axios-retry";

// src/constants.ts
var _baseUrl = "http://localhost:8000";
var _onBaseUrlChange = null;
function _registerBaseUrlListener(listener) {
  _onBaseUrlChange = listener;
}
function configureBaseUrl(url) {
  _baseUrl = url;
  _onBaseUrlChange?.(url);
}
var API_CONFIG = {
  get baseUrl() {
    return _baseUrl;
  },
  timeout: 3e4,
  retryAttempts: 3,
  retryDelay: 1e3
};
var STORAGE_KEYS = {
  authToken: "auth_token",
  refreshToken: "refresh_token",
  sessionId: "session_id"
};
var ERROR_MESSAGES = {
  network: "Network error. Please check your connection and try again.",
  unauthorized: "You are not authorized to perform this action.",
  notFound: "The requested resource was not found.",
  serverError: "Something went wrong. Please try again later.",
  validation: "Please check your input and try again.",
  sessionExpired: "Your session has expired. Please sign in again.",
  posAuthRequired: "This restaurant's payment system is temporarily offline, so adding cards and payments isn't available right now. Please try again later or let the restaurant know.",
  upstreamRejected: "The restaurant's ordering system rejected this order. Please review your cart and try again, or contact the restaurant."
};

// src/storage.ts
var InMemoryStorage = class {
  store = /* @__PURE__ */ new Map();
  getItem(key) {
    return this.store.get(key) ?? null;
  }
  setItem(key, value) {
    this.store.set(key, value);
  }
  removeItem(key) {
    this.store.delete(key);
  }
};
var persistentStorage = null;
var configuredSessionStorage = null;
var configured = false;
var fallback = new InMemoryStorage();
function configureStorage(options) {
  persistentStorage = options.persistent;
  configuredSessionStorage = options.session ?? options.persistent;
  configured = true;
}
function getPersistentStorage() {
  if (!configured) {
    if (typeof globalThis.localStorage !== "undefined") {
      return globalThis.localStorage;
    }
    console.warn("[api-client] Storage not configured, using in-memory fallback");
    return fallback;
  }
  return persistentStorage ?? fallback;
}
function getSessionStorage() {
  if (!configured) {
    if (typeof globalThis.sessionStorage !== "undefined") {
      return globalThis.sessionStorage;
    }
    return getPersistentStorage();
  }
  return configuredSessionStorage ?? fallback;
}
function resetStorageForTesting() {
  persistentStorage = null;
  configuredSessionStorage = null;
  configured = false;
  fallback = new InMemoryStorage();
}

// src/client.ts
function isUpstreamRejection(error) {
  if (error.response?.status !== 502) return false;
  const body = error.response.data;
  return body != null && typeof body === "object" && "clover_status" in body;
}
var ApiClient = class {
  client;
  config;
  _cachedAuthToken = null;
  _cachedSessionId = null;
  refreshPromise = null;
  constructor(config = {}) {
    this.config = {
      baseURL: config.baseURL || API_CONFIG.baseUrl,
      timeout: config.timeout || API_CONFIG.timeout,
      retryAttempts: config.retryAttempts || API_CONFIG.retryAttempts,
      retryDelay: config.retryDelay || API_CONFIG.retryDelay,
      onTokenRefresh: config.onTokenRefresh || (() => Promise.resolve("")),
      onUnauthorized: config.onUnauthorized || (() => {
      })
    };
    this.client = axios.create({
      baseURL: this.config.baseURL,
      timeout: this.config.timeout,
      headers: {
        "Content-Type": "application/json"
      }
    });
    this.setupInterceptors();
    this.setupRetry();
  }
  setupInterceptors() {
    this.client.interceptors.request.use(
      (config) => {
        if (!config.skipAuth) {
          const token = this.getAuthToken();
          if (token && config.headers) {
            config.headers.Authorization = `Bearer ${token}`;
          }
        }
        const sessionId = this.getSessionId();
        if (sessionId && config.headers) {
          config.headers["X-Session-Id"] = sessionId;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );
    this.client.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;
          try {
            if (!this.refreshPromise) {
              this.refreshPromise = this.config.onTokenRefresh();
            }
            const newToken = await this.refreshPromise;
            this.refreshPromise = null;
            if (newToken) {
              this.setAuthToken(newToken);
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
              return this.client(originalRequest);
            }
          } catch {
            if (this.refreshPromise !== null) {
              this.refreshPromise = null;
              this.config.onUnauthorized();
            }
            return Promise.reject(this.createError("unauthorized", error));
          }
        }
        if (error.response) {
          switch (error.response.status) {
            case 404:
              return Promise.reject(this.createError("notFound", error));
            case 409: {
              const body = error.response.data;
              if (body?.error === "pos_auth_required") {
                const mapped = this.createError("posAuthRequired", error);
                mapped.code = "pos_auth_required";
                return Promise.reject(mapped);
              }
              return Promise.reject(error);
            }
            case 422:
            case 400:
              return Promise.reject(this.createError("validation", error));
            case 502: {
              if (isUpstreamRejection(error)) {
                const body = error.response.data;
                const mapped = this.createError("upstreamRejected", error);
                mapped.code = "upstream_rejected";
                mapped.details = typeof body.details === "string" ? body.details : void 0;
                return Promise.reject(mapped);
              }
              return Promise.reject(this.createError("serverError", error));
            }
            case 500:
            case 503:
              return Promise.reject(this.createError("serverError", error));
            default:
              return Promise.reject(error);
          }
        }
        return Promise.reject(this.createError("network", error));
      }
    );
  }
  setupRetry() {
    axiosRetry(this.client, {
      retries: this.config.retryAttempts,
      retryDelay: (retryCount) => {
        return retryCount * this.config.retryDelay;
      },
      retryCondition: (error) => {
        if (isUpstreamRejection(error)) return false;
        return axiosRetry.isNetworkOrIdempotentRequestError(error) || (error.response?.status ? error.response.status >= 500 : false);
      }
    });
  }
  createError(type, originalError) {
    const error = new Error(ERROR_MESSAGES[type]);
    error.originalError = originalError;
    error.type = type;
    error.statusCode = originalError.response?.status;
    return error;
  }
  getAuthToken() {
    const result = getPersistentStorage().getItem(STORAGE_KEYS.authToken);
    if (result instanceof Promise) {
      return this._cachedAuthToken;
    }
    this._cachedAuthToken = result;
    return result;
  }
  setAuthToken(token) {
    this._cachedAuthToken = token;
    void getPersistentStorage().setItem(STORAGE_KEYS.authToken, token);
  }
  getSessionId() {
    const result = getSessionStorage().getItem(STORAGE_KEYS.sessionId);
    if (result instanceof Promise) {
      return this._cachedSessionId;
    }
    this._cachedSessionId = result;
    return result;
  }
  // HTTP methods
  async get(url, config) {
    const response = await this.client.get(url, config);
    return response.data;
  }
  async post(url, data, config) {
    const response = await this.client.post(url, data, config);
    return response.data;
  }
  async put(url, data, config) {
    const response = await this.client.put(url, data, config);
    return response.data;
  }
  async patch(url, data, config) {
    const response = await this.client.patch(url, data, config);
    return response.data;
  }
  async delete(url, config) {
    const response = await this.client.delete(url, config);
    return response.data;
  }
  // Utility methods
  setHeader(key, value) {
    this.client.defaults.headers.common[key] = value;
  }
  removeHeader(key) {
    delete this.client.defaults.headers.common[key];
  }
  getBaseURL() {
    return this.config.baseURL;
  }
  setBaseURL(url) {
    this.config.baseURL = url;
    this.client.defaults.baseURL = url;
  }
  /**
   * Configure auth callbacks on an existing instance.
   * Used to set up token refresh and unauthorized handling on the singleton
   * after app initialization, when Supabase and auth stores are available.
   */
  configure(config) {
    if (config.onTokenRefresh) {
      this.config.onTokenRefresh = config.onTokenRefresh;
    }
    if (config.onUnauthorized) {
      this.config.onUnauthorized = config.onUnauthorized;
    }
  }
  getInstance() {
    return this.client;
  }
  // Auth token management
  setToken(token) {
    this.setAuthToken(token);
  }
  getToken() {
    return this.getAuthToken();
  }
  clearToken() {
    this._cachedAuthToken = null;
    void getPersistentStorage().removeItem(STORAGE_KEYS.authToken);
  }
};
var apiClient = new ApiClient();
_registerBaseUrlListener((url) => apiClient.setBaseURL(url));

// src/endpoints.ts
var API_ENDPOINTS = {
  // Authentication
  auth: {
    login: "/auth/login",
    signup: "/auth/signup",
    logout: "/auth/logout",
    refresh: "/auth/refresh",
    verify: "/auth/verify",
    forgotPassword: "/auth/forgot-password",
    resetPassword: "/auth/reset-password",
    // Phone OTP authentication
    sendPhoneOtp: "/auth/phone/send-otp",
    verifyPhoneOtp: "/auth/phone/verify-otp"
  },
  // User
  user: {
    profile: "/user/profile",
    updateProfile: "/user/profile",
    changePassword: "/user/change-password",
    addresses: "/user/addresses",
    address: (id) => `/user/addresses/${id}`,
    paymentMethods: "/user/payment-methods",
    paymentMethod: (id) => `/user/payment-methods/${id}`,
    preferences: "/user/preferences"
  },
  // Restaurant
  restaurant: {
    list: "/restaurants",
    details: (id) => `/restaurants/${id}`,
    locations: (id) => `/restaurants/${id}/locations`,
    location: (restaurantId, locationId) => `/restaurants/${restaurantId}/locations/${locationId}`,
    nearbyLocations: "/restaurants/locations/nearby"
  },
  // Menu
  menu: {
    get: (restaurantId) => `/restaurants/${restaurantId}/menu`,
    categories: (restaurantId) => `/restaurants/${restaurantId}/categories`,
    items: (restaurantId) => `/restaurants/${restaurantId}/items`,
    item: (restaurantId, itemId) => `/restaurants/${restaurantId}/items/${itemId}`,
    search: (restaurantId) => `/restaurants/${restaurantId}/items/search`,
    popular: (restaurantId) => `/restaurants/${restaurantId}/items/popular`
  },
  // Orders
  orders: {
    create: "/orders",
    place: "/orders/place",
    // Atomic order placement
    list: "/orders",
    details: (id) => `/orders/${id}`,
    update: (id) => `/orders/${id}`,
    cancel: (id) => `/orders/${id}/cancel`,
    track: (id) => `/orders/${id}/track`,
    feedback: (id) => `/orders/${id}/feedback`,
    calculateFees: "/orders/calculate-fees",
    validateCart: "/orders/validate-cart"
  },
  // Payments
  payments: {
    process: "/payments/process",
    intent: "/payments/intent",
    confirm: "/payments/confirm",
    refund: (paymentId) => `/payments/${paymentId}/refund`,
    methods: "/payments/methods",
    saveMethod: "/payments/methods/save"
  },
  // Rewards
  rewards: {
    balance: "/rewards/balance",
    transactions: "/rewards/transactions",
    redeem: "/rewards/redeem",
    available: "/rewards/available",
    tiers: "/rewards/tiers",
    apply: "/rewards/apply"
  },
  // Offers
  offers: {
    list: "/offers",
    details: (id) => `/offers/${id}`,
    apply: "/offers/apply",
    validate: "/offers/validate",
    userOffers: "/offers/user"
  },
  // Delivery
  delivery: {
    quote: "/delivery/quote",
    schedule: "/delivery/schedule",
    track: (deliveryId) => `/delivery/${deliveryId}/track`,
    driver: (deliveryId) => `/delivery/${deliveryId}/driver`,
    update: (deliveryId) => `/delivery/${deliveryId}/update`
  },
  // POS Integration
  pos: {
    createCustomer: (merchantId) => `/pos/${merchantId}/customers`,
    getCustomer: (merchantId, customerId) => `/pos/${merchantId}/customers/${customerId}`,
    getCardDetails: (merchantId, customerId) => `/pos/${merchantId}/customers/${customerId}/cards`,
    saveCard: (merchantId, customerId) => `/pos/${merchantId}/customers/${customerId}/cards`,
    removeCard: (merchantId, customerId) => `/pos/${merchantId}/customers/${customerId}/cards`,
    createOrder: (merchantId) => `/pos/${merchantId}/orders`,
    payOrder: (orderId) => `/pos/orders/${orderId}/pay`,
    refundOrder: (orderId) => `/pos/orders/${orderId}/refund`,
    printOrder: (merchantId, orderId) => `/pos/${merchantId}/orders/${orderId}/print`
  },
  // Miscellaneous
  feedback: {
    submit: "/feedback",
    categories: "/feedback/categories"
  },
  support: {
    contact: "/support/contact",
    faqs: "/support/faqs"
  },
  config: {
    app: "/config/app",
    features: "/config/features",
    pos: "/config/pos"
  },
  version: {
    check: "/check_version"
  }
};

// src/errors.ts
function isAuthExpiredError(error) {
  if (error === null || error === void 0) return false;
  if (typeof error !== "object" && typeof error !== "function") return false;
  return error.type === "unauthorized";
}
function isNotFoundError(error) {
  if (error === null || error === void 0) return false;
  if (typeof error !== "object" && typeof error !== "function") return false;
  return error.type === "notFound";
}
export {
  API_CONFIG,
  API_ENDPOINTS,
  ApiClient,
  ERROR_MESSAGES,
  STORAGE_KEYS,
  _registerBaseUrlListener,
  apiClient,
  configureBaseUrl,
  configureStorage,
  isAuthExpiredError,
  isNotFoundError,
  resetStorageForTesting
};
//# sourceMappingURL=index.js.map